import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(v => toCamelCase(v));
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      result[camelKey] = toCamelCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

const toSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(v => toSnakeCase(v));
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
      result[snakeKey] = toSnakeCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

const isSchemaError = (error) => {
  return error?.code === '42P01' || error?.message?.includes('does not exist');
};

export const supplierRequestService = {
  async create(requestData, items) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Usuario no autenticado');

      // Generate request number
      let requestNumber;
      const { data: rpcNumber, error: numberError } = await supabase
        ?.rpc('generate_supplier_request_number');

      if (numberError) {
        console.error('Error generating request number:', numberError);
        // Fallback: use timestamp-based number
        requestNumber = `SR-${Date.now()}`;
      } else {
        requestNumber = rpcNumber;
      }

      // Calculate totals
      const totalItems = items?.length || 0;
      const totalAmount = items?.reduce((sum, item) => sum + (item?.price || 0), 0) || 0;

      // Create supplier request
      const snakeData = toSnakeCase({
        ...requestData,
        requestNumber,
        totalItems,
        totalAmount,
        createdBy: user?.id,
        status: 'pending'
      });

      let request;
      let requestError;

      // First attempt
      const result1 = await supabase
        ?.from('supplier_requests')
        ?.insert(snakeData)
        ?.select()
        ?.single();

      request = result1?.data;
      requestError = result1?.error;

      // If duplicate key on request_number, retry with timestamp-based number
      if (requestError && (requestError?.code === '23505' || requestError?.message?.includes('duplicate key'))) {
        const fallbackNumber = `SR-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
        const retryData = { ...snakeData, request_number: fallbackNumber };
        const result2 = await supabase
          ?.from('supplier_requests')
          ?.insert(retryData)
          ?.select()
          ?.single();
        request = result2?.data;
        requestError = result2?.error;
      }

      if (requestError) {
        if (isSchemaError(requestError)) {
          console.error('Schema error:', requestError?.message);
          throw requestError;
        }
        throw new Error(requestError.message);
      }

      // Create supplier request items
      if (items && items?.length > 0) {
        const itemsData = items?.map(item => toSnakeCase({
          supplierRequestId: request?.id,
          description: item?.description || item?.name || item?.type || 'Sin descripción',
          quantity: item?.quantity || 1,
          unitPrice: item?.price,
          totalPrice: (item?.price || 0) * (item?.quantity || 1),
          supplier: item?.supplier,
          priority: item?.priority || 'medium'
        }));

        const { error: itemsError } = await supabase
          ?.from('supplier_request_items')
          ?.insert(itemsData);

        if (itemsError) {
          console.error('Error creating request items:', itemsError);
          // Rollback: delete the request
          await supabase?.from('supplier_requests')?.delete()?.eq('id', request?.id);
          throw new Error(itemsError.message);
        }
      }

      return toCamelCase(request);
    } catch (error) {
      console.error('Failed to create supplier request:', error);
      throw error;
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase
        ?.from('supplier_requests')
        ?.select(`
          *,
          items:supplier_request_items(*),
          work_order:work_orders(
            id,
            folio_ot,
            estado_ot,
            vehicle:vehicles(
              marca,
              modelo,
              año,
              placas
            ),
            client:clients(
              nombre,
              email,
              telefono
            )
          )
        `)
        ?.eq('id', id)
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return null;
        throw new Error(error.message);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to fetch supplier request:', error);
      throw error;
    }
  },

  async getByWorkOrder(workOrderId) {
    try {
      const { data, error } = await supabase
        ?.from('supplier_requests')
        ?.select(`
          *,
          items:supplier_request_items(*)
        `)
        ?.eq('work_order_id', workOrderId)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        throw new Error(error.message);
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch supplier requests:', error);
      throw error;
    }
  },

  async getAll() {
    try {
      const { data, error } = await supabase
        ?.from('supplier_requests')
        ?.select(`
          *,
          items:supplier_request_items(*),
          work_order:work_orders(
            folio_ot,
            vehicle:vehicles(marca, modelo, año)
          )
        `)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        throw new Error(error.message);
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch supplier requests:', error);
      throw error;
    }
  },

  async updateStatus(id, status) {
    try {
      const { data, error } = await supabase
        ?.from('supplier_requests')
        ?.update({ status })
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to update supplier request status:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase
        ?.from('supplier_requests')
        ?.delete()
        ?.eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return true;
    } catch (error) {
      console.error('Failed to delete supplier request:', error);
      throw error;
    }
  }
};